/**
 * Boot and crash (doc 18 §5, §6) — the two screens nobody designs and everybody
 * needs.
 *
 * The loading screen is DOM, not canvas, on purpose: doc 18 wants first paint
 * under a second, and a canvas screen cannot appear until the renderer exists.
 * So the markup ships with the page, this drives its bar, and it is removed the
 * moment the game is actually interactive.
 *
 * The crash screen exists so a crash never costs meta progress. It offers the
 * two things that actually help — the diagnostics, and the save — and it says
 * so without solemnity, which is doc 01's tone contract even here.
 */

import { S } from '../content/strings.ts';

const TONE = S.boot.somethingImplodedNot;

/** Steps the loading bar reports, in order. Each one is a real wait. */
export type BootStep = 'boot' | 'renderer' | 'atlases' | 'save' | 'ready';

const LABELS: Record<BootStep, { text: string; pct: number }> = {
  boot: { text: S.boot.pressurizing, pct: 8 },
  renderer: { text: S.boot.floodingChamber, pct: 35 },
  atlases: { text: S.boot.printingFish, pct: 68 },
  save: { text: S.boot.findingColony, pct: 88 },
  ready: { text: S.boot.descending, pct: 100 },
};

export function bootProgress(step: BootStep): void {
  const label = document.getElementById('boot-label');
  const bar = document.getElementById('boot-bar');
  if (label) label.textContent = LABELS[step].text;
  if (bar) bar.style.width = `${LABELS[step].pct}%`;
}

export function bootDone(): void {
  document.getElementById('boot')?.remove();
}

/**
 * Doc 18 §6: a crash never leaves a blank canvas with no explanation, and never
 * costs the player their colony. `exportSave` is passed in rather than imported
 * so this file works before — and after — the save layer is alive.
 */
export function installCrashScreen(exportSave: () => string | null): void {
  const show = (what: string, detail: unknown): void => {
    if (document.getElementById('crash')) return; // one crash screen, not five
    bootDone(); // a crash mid-boot must not leave the loading bar underneath it
    const diagnostics = [
      `KRILL ISSUES — ${what}`,
      String(detail instanceof Error ? (detail.stack ?? detail.message) : detail),
      `ua: ${navigator.userAgent}`,
      `viewport: ${window.innerWidth}x${window.innerHeight} dpr ${devicePixelRatio}`,
      `url: ${location.href}`,
    ].join('\n');
    document.body.append(crashScreen(diagnostics, exportSave));
  };

  window.addEventListener('error', (e) => show(S.boot.uncaughtError, e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => show(S.boot.unhandledRejection, e.reason));
}

/** Also used by the boot failure path, which is a crash with a nicer cause. */
export function crashScreen(diagnostics: string, exportSave: () => string | null): HTMLElement {
  const el = document.createElement('div');
  el.id = 'crash';
  el.setAttribute(
    'style',
    'position:fixed;inset:0;z-index:9;display:grid;place-content:center;gap:16px;' +
      'background:#020508;color:#e8f4f2;font:12px/1.7 ui-monospace,monospace;letter-spacing:.12em;' +
      'text-align:center;padding:32px',
  );

  const head = document.createElement('div');
  head.textContent = TONE;
  head.setAttribute('style', 'font-size:18px;letter-spacing:.2em;color:#ff4d6a');

  const body = document.createElement('div');
  body.textContent = S.boot.pearlsAreDisk;
  body.setAttribute('style', 'color:#8fa8ad;max-width:56ch');

  const row = document.createElement('div');
  row.setAttribute('style', 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap');
  row.append(
    button(S.boot.copyDiagnostics, () => copy(diagnostics, row)),
    button(S.boot.exportSave, () => copy(exportSave() ?? S.boot.noSaveThis, row)),
    button(S.boot.reload, () => location.reload()),
  );

  const trace = document.createElement('pre');
  trace.textContent = diagnostics;
  trace.setAttribute(
    'style',
    'max-width:70ch;max-height:24vh;overflow:auto;text-align:left;color:#8fa8ad;' +
      'background:#0a1c24;padding:12px;font-size:10px;letter-spacing:0',
  );

  el.append(head, body, row, trace);
  return el;
}

function button(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.setAttribute(
    'style',
    'background:#0a1c24;color:#e8f4f2;border:1px solid #3f7180;padding:8px 14px;' +
      'font:12px ui-monospace,monospace;letter-spacing:.12em;cursor:pointer',
  );
  b.addEventListener('click', onClick);
  return b;
}

function copy(text: string, host: HTMLElement): void {
  void navigator.clipboard
    .writeText(text)
    .then(() => note(host, S.boot.copied))
    .catch(() => note(host, S.boot.browserRefusedClipboard));
}

function note(host: HTMLElement, text: string): void {
  const existing = host.parentElement?.querySelector('#crash-note');
  const el = existing ?? document.createElement('div');
  el.id = 'crash-note';
  el.textContent = text;
  el.setAttribute('style', 'color:#5ff2c8');
  if (!existing) host.after(el);
}
